import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  setPersistence,
  browserLocalPersistence,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type Auth,
} from 'firebase/auth';

const required = (key: string): string => {
  const v = import.meta.env[key as keyof ImportMetaEnv];
  if (!v) {
    throw new Error(
      `Missing ${key}. Copy .env.example to .env.local and fill in the Firebase web config from the Firebase Console.`,
    );
  }
  return v as string;
};

const firebaseConfig = {
  apiKey: required('VITE_FIREBASE_API_KEY'),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: required('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: required('VITE_FIREBASE_APP_ID'),
};

const ALLOWED_DOMAIN = (import.meta.env.VITE_ALLOWED_DOMAIN as string) || 'henoyo.ai';

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);

const provider = new GoogleAuthProvider();
// `hd` is a Google account-chooser hint — UX only, NOT a security gate.
// The real enforcement happens in the Cloud Function via verifyIdToken +
// email_verified + (staff domain OR allowlist). Partners without a Google
// account use the email-link ("magic link") flow below.
provider.setCustomParameters({ hd: ALLOWED_DOMAIN, prompt: 'select_account' });

// Persistent session ("stay signed in until logout"). browserLocalPersistence is
// the web default, but we set it explicitly before any sign-in method runs.
const persistenceReady = setPersistence(auth, browserLocalPersistence).catch(() => {});

// localStorage key for the email used to request a magic link (Firebase needs it
// to complete sign-in on the same device).
const EMAIL_LINK_KEY = 'henoyo:emailForSignIn';

export async function signInWithGoogle() {
  await persistenceReady;
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

export async function signOut() {
  await fbSignOut(auth);
}

/**
 * Request a passwordless sign-in link. Our backend (POST /api/request-link)
 * generates the secure link with the Admin SDK and sends a Henoyo-branded email
 * from hello@henoyo.ai — only to staff/allowlisted addresses (uniform 200 either
 * way, so this never reveals eligibility). The link is completed by the existing
 * completeEmailLinkSignIn flow on this device.
 */
export async function sendSignInLink(email: string): Promise<void> {
  const e = email.trim().toLowerCase();
  const res = await fetch('/api/request-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify({ email: e }),
  });
  if (!res.ok) throw new Error('Could not send the sign-in link. Please try again.');
  // Store the email so completion works when the link is opened on this device.
  window.localStorage.setItem(EMAIL_LINK_KEY, e);
}

export function isEmailSignInLink(href: string): boolean {
  return isSignInWithEmailLink(auth, href);
}

export function getStoredEmailForSignIn(): string | null {
  return window.localStorage.getItem(EMAIL_LINK_KEY);
}

export function clearStoredEmailForSignIn(): void {
  window.localStorage.removeItem(EMAIL_LINK_KEY);
}

/** Complete a magic-link sign-in. `href` must be the full URL containing the oobCode. */
export async function completeEmailLinkSignIn(email: string, href: string) {
  await persistenceReady;
  const e = email.trim().toLowerCase();
  const result = await signInWithEmailLink(auth, e, href);
  window.localStorage.removeItem(EMAIL_LINK_KEY);
  return result.user;
}
